import type {ReactElement} from 'react';
import {useMemo, useState, useEffect, useRef} from 'react';
import styles from './ai-chat-landing-hero.module.scss';
import type {AiChatLandingHeroProps} from './ai-chat-landing-hero.types';
import {AiChatLandingHeroSize} from './ai-chat-landing-hero.types';
import {LANDING_TIP_CONFIGS, GREETING_MESSAGES_WITH_NAME, GREETING_MESSAGES_WITHOUT_NAME} from './ai-chat-landing-tip-config';
import {getUserFirstName} from '@Utils/user.util';
import {getSeenTipsFromLocalStorage, fetchSeenTipsFromBackend, getRecentlyShownLandingTip, setRecentlyShownLandingTip, markTipSeenInLocalStorage, markTipSeenOnBackend} from '@Components/ai-prompt-box/hooks/pro-tips/pro-tip-persistence';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function AiChatLandingHero({className, size = AiChatLandingHeroSize.LARGE, hideTip = false, refreshKey}: AiChatLandingHeroProps): ReactElement {
  const [seenTips, setSeenTips] = useState<Set<string>>(() => getSeenTipsFromLocalStorage());
  const [isLoading, setIsLoading] = useState(true);
  const backendFetched = useRef(false);

  useEffect(() => {
    if (backendFetched.current) {
      return;
    }
    backendFetched.current = true;

    void fetchSeenTipsFromBackend().then((response) => {
      setSeenTips((prev) => {
        const merged = new Set(prev);
        response.tips.forEach((tip: string) => merged.add(tip));
        return merged;
      });
      setIsLoading(false);
    });
  }, []);

  const greeting = useMemo((): string => {
    const firstName = getUserFirstName();
    if (firstName) {
      const key = pickRandom(GREETING_MESSAGES_WITH_NAME);
      return window.i18next.t(key, {name: firstName});
    }

    const key = pickRandom(GREETING_MESSAGES_WITHOUT_NAME);
    return window.i18next.t(key);
  }, [refreshKey]);

  const selectedTip = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const sortedConfigs = [...LANDING_TIP_CONFIGS].sort((a, b) => a.priority - b.priority);
    const recentlyShownType = getRecentlyShownLandingTip();
    const unseenTip = sortedConfigs.find((config) => !seenTips.has(config.type) && config.type !== recentlyShownType);

    if (unseenTip) {
      return unseenTip;
    }

    return sortedConfigs.find((config) => !seenTips.has(config.type)) ?? null;
  }, [seenTips, isLoading, refreshKey]);

  useEffect(() => {
    if (selectedTip) {
      setRecentlyShownLandingTip(selectedTip.type);
      markTipSeenOnBackend(selectedTip.type);
      markTipSeenInLocalStorage(selectedTip.type);
      setSeenTips((prev) => new Set([...prev, selectedTip.type]));
    }
  }, [selectedTip]);

  const tipMessage = selectedTip ? window.i18next.t(selectedTip.message) : null;

  function getHeadingClassName(): string {
    const sizeClass = size === AiChatLandingHeroSize.SMALL ? styles.headingSmall : size === AiChatLandingHeroSize.MEDIUM ? styles.headingMedium : '';
    return `${styles.heading} ${sizeClass}`;
  }

  const hasUnseenTipInLocalStorage = useMemo((): boolean => {
    const localSeen = getSeenTipsFromLocalStorage();
    return LANDING_TIP_CONFIGS.some((config) => !localSeen.has(config.type));
  }, []);

  function renderTip(): ReactElement | null {
    if (size === AiChatLandingHeroSize.SMALL) {
      return null;
    }

    if (isLoading) {
      if (!hasUnseenTipInLocalStorage) {
        return null;
      }
      return <SkeletonBrick width={280} height={20} className="radius-8" theme={SkeletonBrickTheme.AI_GRADIENT} />;
    }

    if (!tipMessage) {
      return null;
    }

    const isVisible = !hideTip;

    return (
      <div className={`${styles.tipWrapper} ${isVisible ? styles.tipVisible : ''}`}>
        <p className={styles.tipText}>{`\u{1F4A1} ${window.i18next.t('pmwjs_landing_tip_prefix')}: ${tipMessage}`}</p>
      </div>
    );
  }

  function renderSparkleIcon(): ReactElement {
    const iconSize = size === AiChatLandingHeroSize.LARGE ? IconSize.SIZE_ICON_32 : size === AiChatLandingHeroSize.MEDIUM ? IconSize.SIZE_ICON_24 : IconSize.SIZE_ICON_20;

    return <Icon icon="icon-sparkle-filled" className={styles.sparkleIcon} size={iconSize} type={IconType.FLAT} aria-hidden />;
  }

  return (
    <div className={`${styles.heroContainer} ${className ?? ''}`}>
      <h3 className={getHeadingClassName()}>
        {greeting}
        {'\u00A0'}
        {renderSparkleIcon()}
      </h3>
      {renderTip()}
    </div>
  );
}
